import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/** 微信端用户简要信息（登录后保存） */
export interface IWechatUserInfo {
    id: number;
    username: string;
    realName: string;
    roles: string[];
}

/** 本地持久化的登录态键名 */
const TOKEN_KEY = 'wechat_token';
const USER_KEY = 'wechat_user';

/**
 * 微信 H5 端用户状态
 *
 * 与桌面 Web 端相互独立（localStorage 键不同），JWT 与 Web 端同一套认证体系
 * 持久化策略：token + user 存 localStorage，刷新免登录
 */
export const useUserStore = defineStore('wechatUser', () => {
    const token = ref<string>(localStorage.getItem(TOKEN_KEY) || '');
    const userInfo = ref<IWechatUserInfo | null>(loadUserFromStorage());

    const isLoggedIn = computed<boolean>(() => !!token.value && !!userInfo.value);
    const realName = computed<string>(() => userInfo.value?.realName ?? '');
    const roles = computed<string[]>(() => userInfo.value?.roles ?? []);

    /**
     * 保存登录态（OAuth 登录/绑定成功后调用）
     */
    function setLoginInfo(newToken: string, user: IWechatUserInfo): void {
        token.value = newToken;
        userInfo.value = user;
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    /**
     * 清除登录态（token 过期/解绑时调用）
     */
    function clearLogin(): void {
        token.value = '';
        userInfo.value = null;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    }

    return { token, userInfo, isLoggedIn, realName, roles, setLoginInfo, clearLogin };
});

/** 从 localStorage 恢复用户信息 */
function loadUserFromStorage(): IWechatUserInfo | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as IWechatUserInfo;
    } catch {
        return null;
    }
}
